import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/ui/use-toast";
import { Trash2 } from "lucide-react";

interface Project {
  id: number;
  title: string;
  client: string;
  types: string[];
  image: string;
  link: string;
  embedCode: string;
}

interface ProjectListProps {
  projects: Project[];
  onDelete?: (id: number) => void;
  isClientView?: boolean;
}

export const ProjectList = ({ projects, onDelete, isClientView = false }: ProjectListProps) => {
  const { toast } = useToast();

  const handleCopyCode = (embedCode: string) => {
    navigator.clipboard.writeText(embedCode);
    toast({
      title: "Copied!",
      description: "Embed code copied to clipboard",
    });
  };

  return (
    <div className="space-y-4 sm:space-y-6 animate-fadeIn">
      {!isClientView && <h2 className="text-xl sm:text-2xl font-bold text-white">Created Projects</h2>}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {projects.map((project) => (
          <Card key={project.id} className="p-4 sm:p-6 space-y-4 bg-black/50 backdrop-blur-sm border-gray-800">
            <div className="space-y-3">
              <div className="flex justify-between items-start">
                <h3 className="text-lg sm:text-xl font-semibold text-white">{project.title}</h3>
                {!isClientView && onDelete && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onDelete(project.id)}
                    className="h-8 w-8 text-gray-400 hover:text-red-500 hover:bg-red-500/10"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <p className="text-sm text-gray-400">Client: {project.client}</p>
            </div>
            
            <div className="flex flex-wrap gap-2">
              {project.types.map((type) => (
                <Badge key={type} variant="secondary" className="bg-gray-800 text-white border-0">
                  #{type.toLowerCase()}
                </Badge>
              ))}
            </div>
            
            {project.image && (
              <img
                src={project.image}
                alt={project.title}
                className="w-full h-32 sm:h-48 object-cover rounded-md"
              />
            )}
            
            {!isClientView && (
              <>
                <div className="bg-black/70 p-3 sm:p-4 rounded-md">
                  <code className="text-xs sm:text-sm break-all text-gray-300">{project.embedCode}</code>
                </div>
                
                <Button
                  onClick={() => handleCopyCode(project.embedCode)}
                  className="w-full bg-gradient-primary text-white border-0 hover:opacity-90"
                >
                  Copy Embed Code
                </Button>
              </>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
};
